import { isInGroup, removeObjectFromCanvas, removeRepeatingLinkParams, updateBoundingBoxInfo } from '@frontend/utils';
import { STATIC_IMAGE_PREFIX_ID } from '@frontend/constants/templates-field-prefixes';
import { ShowPageScopeCollector } from '@frontend/group/modules/scope-collectors/show-page-collector';
import { fabric } from 'fabric';
import { formDataToObject } from '@frontend/lib/utils/form-data-to-object';
import { getGroupNameByAddFieldId } from "@/common/preview/draw-smart-object";

const getScopePage = () => ShowPageScopeCollector.get(['canvas_data', 'base_url', 'getIsLightweight']);

export function drawStaticImage({
   fields,
   data,
   canvas,
   id,
   group_fields,
   dimension,
   previewBoundingBoxData
}) {
  const { canvas_data, base_url } = getScopePage();
  const formData = new FormData();
  formData.append('type', 'Static Image');
  return new Promise((resolve, reject) => {
    removeObjectFromCanvas(canvas, STATIC_IMAGE_PREFIX_ID);
    canvas_data[id].stImgCoords = [];
    let index = 0;
    let promises = [];

    fields.forEach((field) => {
      const options = JSON.parse(field.options);
      const groupName = getGroupNameByAddFieldId(
        String(field.id),
        canvas_data[id]
      );
      
      if (field.type === 'Static Image' && (!group_fields || isInGroup(group_fields, field.name))) {
        const fid = field.element_id;
        // if File is not uploaded, we can use placeholder
        let url = base_url + '/share?file=' + (options['Filename'] || options['Placeholder']);
        const offset_x = data[`${fid}_offset_x`];
        const offset_y = data[`${fid}_offset_y`];
        const angle = data[`${fid}_angle`];
        const scale = parseFloat(data[`${fid}_scale`]);
      
        const extension =  options['Filename'] ? options['Filename'].split('.').slice(-1)[0] : null;
        if (extension !== null && extension.toLowerCase() === 'svg') {
          const filename = options['Filename'].split('/').slice(-1)[0];
          url = base_url + '/img/upload/' + filename;
          promises.push(new Promise((resolve, reject) => {
            fabric.loadSVGFromURL(url, function (objects, opt) {
              const oImg = fabric.util.groupSVGElements(objects, opt);
              const r = oImg.width / parseInt(options['Width']);
              const r1 = oImg.height / r / parseInt(options['Height']);
              
              oImg.set({
                id: STATIC_IMAGE_PREFIX_ID + index,
                groupName: groupName,
                element_id: field.element_id,
                order: parseInt(options['Order']),
                left: canvas_data[id].spacingFieldPosition[field.name]
                  ? canvas_data[id].spacingFieldPosition[field.name].x
                  : parseInt(options['X']) + parseFloat(offset_x),
                top: parseInt(options['Y']) + parseFloat(offset_y),
                angle: parseInt(angle),
                selectable: options['Moveable'] === 'Yes',
                evented: options['Moveable'] === 'Yes',
                fieldId: options['id']
              });
              
              oImg.scaleToWidth(oImg.width / r / r1);
              oImg.scaleToHeight(oImg.height / r / r1);
              
              oImg.set({
                scaleX: oImg.scaleX * scale,
                scaleY: oImg.scaleY * scale
              });

              let group_x = 0;
              let group_y = 0;
              if (groupName !== '' && canvas_data[id].smartObjCoords[groupName]) {
                group_x = parseInt(canvas_data[id].smartObjCoords[groupName]['X']);
                group_y = parseInt(canvas_data[id].smartObjCoords[groupName]['Y']);
              }
              
              oImg.set({
                left: oImg.left + group_x - dimension[2] + dimension[4].x,
                top: oImg.top + group_y - dimension[3] + dimension[4].y
              });

              canvas.add(oImg);
              
              canvas_data[id].stImgCoords.push({
                x: parseInt(options['X']) + group_x - dimension[2] + dimension[4].x,
                y: parseInt(options['Y']) + group_y - dimension[3] + dimension[4].y,
                scaleX: oImg.scaleX / scale,
              });
              
              index++;
              
              resolve();
            });
          }));
        } else {
          // replace domain name from URL POR-2222
          // Only for Filename, not Placeholder
          if (url && url.includes('share?file=') && options['Filename'] !== '') {
            url = removeRepeatingLinkParams(url, '&full_resolution');
          }
          
          promises.push(new Promise((resolve, reject) => {
            const { getIsLightweight } = getScopePage();
            const is_lightweight = getIsLightweight();
            
            fabric.Image.fromURL(url, function (oImg) {
              const r = oImg.width / parseInt(options.Width);
              const r1 = oImg.height / r / parseInt(options.Height);
              
              oImg.set({
                id: STATIC_IMAGE_PREFIX_ID + index,
                groupName: groupName,
                element_id: fid,
                left: parseInt(options.X) + parseFloat(offset_x),
                top: parseInt(options.Y) + parseFloat(offset_y),
                angle: parseInt(angle),
                order: parseInt(options.Order),
                selectable: options.Moveable === 'Yes',
                evented: options.Moveable === 'Yes',
                fieldId: options['id']
              });
              
              oImg.scaleToWidth(oImg.width / r / r1);
              oImg.scaleToHeight(oImg.height / r / r1);
              
              oImg.set({
                scaleX: oImg.scaleX * scale,
                scaleY: oImg.scaleY * scale
              });

              let group_x = 0;
              let group_y = 0;
              if (groupName !== '' && canvas_data[id].smartObjCoords[groupName]) {
                group_x = parseInt(canvas_data[id].smartObjCoords[groupName]['X']);
                group_y = parseInt(canvas_data[id].smartObjCoords[groupName]['Y']);
              }
              
              oImg.set({
                left: oImg.left + group_x - dimension[2] + dimension[4].x,
                top: oImg.top + group_y - dimension[3] + dimension[4].y
              });

              canvas.add(oImg);

              formData.append(oImg.element_id + '_bounding_data', JSON.stringify(oImg.getBoundingRect()));
              formData.append(oImg.element_id + '_width', oImg.getScaledWidth());
              formData.append(oImg.element_id + '_height', oImg.getScaledHeight());

              canvas_data[id].stImgCoords.push({
                x: parseFloat(options.X),
                y: parseFloat(options.Y),
                scaleX: oImg.scaleX / scale,
              });
              
              index++;
              
              resolve();
            });
          }));
        }
      }
    });
    
    Promise.all(promises).then(() => {
      resolve();
      
      if (previewBoundingBoxData) {
        previewBoundingBoxData.data.push(formDataToObject(formData));
      } else {
        updateBoundingBoxInfo(formData, id);
      }
    });
  });
}
